import { Component, NgModule, ViewContainerRef } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { FieldDataUpsertOperationInterface } from '@vendasta/account-group';
import { FieldHandler, FieldHandlerValidator } from '../auxiliary-data-table/auxiliary-data-table.interface';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { ReadOnlyStyles } from './common';

export class TextFieldHandler implements FieldHandler {
  formControl: UntypedFormControl;

  constructor(
    public fieldId: string,
    public name: string,
    public label: string,
    public value: string,
    public description: string,
    public validators: FieldHandlerValidator[],
    public readOnly = false,
  ) {
    this.formControl = new UntypedFormControl(this.value);
    this.formControl.setValidators(validators.map((v) => v.validatorFn));
  }

  hasModification(): boolean {
    return this.formControl?.dirty;
  }

  upsertOperation(): FieldDataUpsertOperationInterface {
    if (this.formControl.value === null) {
      return {
        fieldDataId: this.fieldId,
        deleteValue: true,
      };
    }
    return {
      fieldDataId: this.fieldId,
      string: this.formControl.value,
    };
  }

  render(viewContainerRef: ViewContainerRef): void {
    viewContainerRef.clear();
    const component = viewContainerRef.createComponent<TextRendererComponent>(TextRendererComponent);
    component.instance.control = this.formControl;
    component.instance.label = this.label;
    component.instance.validators = this.validators;
    component.instance.readOnly = this.readOnly;
  }
}

@Component({
  styles: [
    `
      :host,
      mat-form-field,
      input {
        width: 100%;
      }

      :host ::ng-deep .mat-form-field-appearance-outline .mat-form-field-flex {
        margin-top: 0;
      }

      #text {
        margin: 0 5px;
        padding: 6px 0 8px;
      }

      #text ::ng-deep .input-and-hints {
        margin: 0;
      }

      :host .value {
        white-space: pre-wrap;
      }
    `,
    ...ReadOnlyStyles,
  ],
  template: `
    <div *ngIf="readOnly; else formField">
      <div class="label">
        {{ label }}
      </div>
      <div class="value">
        {{ control.value ? control.value : '-' }}
      </div>
    </div>
    <ng-template #formField>
      <glxy-form-field [size]="'large'" id="text">
        <glxy-label *ngIf="label">{{ label }}</glxy-label>
        <textarea
          matInput
          [formControl]="control"
          #textInput="matInput"
          maxlength="4000"
          [placeholder]="'COMMON.ADD_FIELD' | translate: { fieldName: label?.toLowerCase() }"
        ></textarea>
        <glxy-hint align="end" *ngIf="textInput.focused">
          {{ 'AUXILIARY_DATA_TABLE.STRING_LIMIT_HINT' | translate: { characters: control?.value?.length || 0 } }}
        </glxy-hint>
        <ng-container *ngFor="let validator of validators" ngProjectAs="mat-error">
          <glxy-error *ngIf="control.hasError(validator.errorCode)">
            {{ validator.errorMessage | translate }}
          </glxy-error>
        </ng-container>
      </glxy-form-field>
    </ng-template>
  `,
  standalone: false,
})
export class TextRendererComponent {
  control!: UntypedFormControl;
  label!: string;
  validators!: FieldHandlerValidator[];
  readOnly!: boolean;
}

@NgModule({
  declarations: [TextRendererComponent],
  exports: [TextRendererComponent],
  imports: [CommonModule, MatInputModule, ReactiveFormsModule, TranslateModule, GalaxyFormFieldModule],
})
export class TextRendererComponentModule {}
